import React from 'react';
import { StyleSheet, Text as RNText, View } from 'react-native';

import { LogoImage } from './logo-image';

/**
 * Printable #10 business envelope (9.5" × 4.125") — issue #172. Shared by the
 * bulk mailings run (`app/print/statements/index.tsx`, envelope mode) and the
 * per-customer envelope print. Pure given a recipient + return block; the
 * wrapper owns the `@page` size, auto-print, and page breaks between envelopes.
 *
 * Layout mirrors the manual QuickBooks→Word mail-merge it replaces:
 *   - return address top-left (small)
 *   - recipient address block centered in the lower-right quadrant
 *   - optional red notice (e.g. "ANNUAL MEETING NOTICE") bottom-left in red
 *
 * Tags its root with `data-envelope-sheet="1"` so the wrapper's print CSS can
 * force a page break between envelopes.
 */

export interface EnvelopeRecipient {
	/** Optional legal/company/trust name printed ABOVE the owner name (#227). */
	companyName?: string;
	/** Bold first line — owner name(s). */
	name: string;
	/** Optional "c/o" line (per-account, part of the billing address). */
	coLine?: string;
	/** Remaining address lines (street, city/state/zip), pre-split. */
	addressLines: string[];
}

/** Default return-address color (dark blue) when the org hasn't set a brand color. */
export const DEFAULT_BRAND_COLOR = '#0d47a1';

interface Props {
	recipient: EnvelopeRecipient;
	/** Return-address block (org name + office address), pre-split into lines. */
	returnLines: string[];
	/** Optional attention notice printed in red at the lower-RIGHT corner. */
	redNotice?: string;
	/** Optional org logo (base64 data URL) printed top-left above the return address. */
	logo?: string;
	/** Return-address text color (hex). Defaults to dark blue to match the logo. */
	brandColor?: string;
	/**
	 * Print the logo + return address. OFF by default — most orgs (e.g. SBS) use
	 * envelopes with the return address & logo PRE-PRINTED, so we only add the
	 * recipient. Turn on to print the return block onto blank envelopes.
	 */
	showReturnAddress?: boolean;
}

export const EnvelopeSheet: React.FC<Props> = ({
	recipient, returnLines, redNotice, logo, brandColor, showReturnAddress = false,
}) => {
	const returnColor = brandColor || DEFAULT_BRAND_COLOR;
	return (
		<View style={styles.envelope} {...({ dataSet: { envelopeSheet: '1' } } as any)}>
			{showReturnAddress ? (
				<View style={styles.returnBlock}>
					{logo ? <View style={styles.logoWrap}><LogoImage uri={logo} maxHeight={72} maxWidth={300} /></View> : null}
					{returnLines.map((line, i) => (
					// Without a logo the first line is the company name (bold). With a
					// logo present, the art carries the name and every line is address.
						<RNText key={i} style={[(!logo && i === 0) ? styles.returnName : styles.returnLine, { color: returnColor }]} numberOfLines={1}>
							{line}
						</RNText>
					))}
				</View>
			) : null}

			<View style={styles.recipientBlock}>
				{/* The address group shrinks to its widest line; the notice is
				    absolutely anchored to its bottom-right corner (top/left: 100%)
				    so it sits BELOW the address with its left edge at the address's
				    right edge. Pure layout → correct on the first paint (no onLayout
				    race with auto-print); the wide box lets the text render in full. */}
				<View style={styles.addrGroup}>
					{recipient.companyName ? <RNText style={styles.recipientName} numberOfLines={1}>{recipient.companyName}</RNText> : null}
					<RNText style={styles.recipientName} numberOfLines={1}>{recipient.name || '—'}</RNText>
					{recipient.coLine ? <RNText style={styles.recipientLine} numberOfLines={1}>c/o {recipient.coLine}</RNText> : null}
					{recipient.addressLines.map((line, i) => (
						<RNText key={i} style={styles.recipientLine} numberOfLines={1}>{line}</RNText>
					))}
					{redNotice ? <RNText style={styles.notice}>{redNotice}</RNText> : null}
				</View>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	// On screen the envelope renders as a bordered card at a readable scale; the
	// wrapper's @media-print CSS strips the chrome and the @page rule pins the
	// physical 9.5"×4.125" size. aspectRatio keeps the on-screen preview true.
	envelope: {
		width: '100%', maxWidth: 912, aspectRatio: 9.5 / 4.125,
		backgroundColor: '#fff', borderWidth: 1, borderColor: '#cfd8dc', borderRadius: 4,
		padding: 28, position: 'relative',
	},
	returnBlock: { position: 'absolute', top: 24, left: 28 },
	logoWrap: { marginBottom: 6, alignItems: 'flex-start' },
	returnName: { fontSize: 11, fontWeight: '700', color: '#000' },
	returnLine: { fontSize: 10, color: '#000' },
	// Lower-right quadrant — standard recipient zone on a #10 envelope.
	recipientBlock: {
		position: 'absolute', left: '38%', right: 36, top: '46%',
	},
	// Shrinks to its widest line and is the positioning context for the notice,
	// which anchors to its bottom-right corner.
	addrGroup: { alignSelf: 'flex-start', position: 'relative' },
	recipientName: { fontSize: 15, fontWeight: '700', color: '#000' },
	recipientLine: { fontSize: 14, color: '#000' },
	// Anchored to the address group's bottom-right corner: below the address,
	// left edge at the address's right edge. The fixed width lets the text lay
	// out in full (no shrink-to-fit clipping) and wrap if it's long; the empty
	// remainder is transparent and overflows harmlessly. ~1.5× the address text.
	notice: {
		position: 'absolute', top: '100%', left: '100%', width: 340, marginLeft: 14, marginTop: 4,
		fontSize: 16, fontWeight: '700', color: '#c62828', letterSpacing: 0.5,
	},
});

export default EnvelopeSheet;
